import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { CITIES, CATEGORIES } from '../config/datamaps';
import { API_BASE_URL, IMAGE_BASE_URL } from "../urldata.js";
import AppBanner from '../components/display/AppBanner';
import SimilarItemsBar from '../components/SimilarItemsBar';
import SEO from '../components/seo/SEO';
import SEOSchema from '../components/seo/SEOSchema';
import Breadcrumbs from '../components/navigation/Breadcrumbs';

function ItemView() {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchItemDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_BASE_URL}/tapi/item/get/${itemId}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setItem(data);
        setActiveImage(0);
      } catch (err) {
        console.error("Failed to fetch item details:", err);
        setError(err.message || "Failed to fetch item details.");
      } finally {
        setLoading(false);
      }
    };

    if (itemId) {
      fetchItemDetails();
    }
  }, [itemId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen || !item?.images?.length) return;

      if (e.key === 'ArrowRight') {
        setActiveImage((prev) => (prev + 1) % item.images.length);
      } else if (e.key === 'ArrowLeft') {
        setActiveImage((prev) => (prev - 1 + item.images.length) % item.images.length);
      } else if (e.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, item]);

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div><p className="mt-2">Loading item details...</p></div>;
  if (error) return <div className="container py-5"><div className="alert alert-danger">Error: {error}</div></div>;
  if (!item) return <div className="container py-5"><div className="alert alert-warning">Item not found.</div></div>;

  const currentImageUrl = item.images && item.images.length > 0
    ? `${IMAGE_BASE_URL}/${item.id}-${item.images[activeImage].image_path}.webp`
    : null;

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": item.title,
    "image": item.images?.map(img => `${IMAGE_BASE_URL}/${item.id}-${img.image_path}.webp`),
    "description": item.description,
    "sku": item.id,
    "brand": {
      "@type": "Brand",
      "name": "ToyTrade"
    },
    "offers": {
      "@type": "Offer",
      "url": window.location.href,
      "priceCurrency": "PKR",
      "price": item.price,
      "itemCondition": item.isnew ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
      "availability": "https://schema.org/InStock"
    }
  };

  const breadcrumbItems = [
    { label: 'Shop', link: '/items' },
    { label: CATEGORIES[item.category] || 'Items', link: `/itemlist?category=${item.category}` },
    { label: item.title }
  ];

  return (
    <div className="  py-4">
      <SEO
        title={`${item.title} - Buy Online in ${CITIES[item.city]} | ToyTrade.pk`}
        description={`${item.title} available for Rs. ${item.price.toLocaleString()} in ${CITIES[item.city]}. ${item.description.substring(0, 150)}...`}
        canonical={window.location.href}
        image={currentImageUrl}
        type="product"
      />
      <SEOSchema data={productSchema} />

      <div className="row">
        {/* Left Side: Product Gallery */}
        <div className="col-lg-6 mb-4">
          <div className="product-gallery">
            {item.images && item.images.length > 1 && (
              <div className="thumbnail-wrapper position-relative text-center">
                <div className="thumb-indicator thumb-up mb-1 d-none d-lg-block">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-up" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z" />
                  </svg>
                </div>
                <div className="thumbnail-list">
                  {item.images.map((image, index) => (
                    <div
                      key={image.id || index}
                      className={`thumbnail-item ${activeImage === index ? 'active' : ''}`}
                      onClick={() => setActiveImage(index)}
                    >
                      <img src={`${IMAGE_BASE_URL}/${item.id}-${image.image_path}.webp`} alt={`${item.title} thumbnail ${index}`} />
                    </div>
                  ))}
                </div>
                <div className="thumb-indicator thumb-down mt-1 d-none d-lg-block">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-down" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                  </svg>
                </div>
              </div>
            )}

            <div className="main-image-container" onClick={() => setIsModalOpen(true)}>
              {currentImageUrl ? (
                <img src={currentImageUrl} alt={item.title} />
              ) : (
                <div className="text-muted">No Image Available</div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Product Details */}
        <div className="col-lg-6 details-right text-start">
          <Breadcrumbs items={breadcrumbItems} />

          <h1 className="fw-bold mb-2">{item.title}</h1>
          <h2 className="text-primary fw-bold mb-4">Rs. {item.price.toLocaleString()}</h2>

          <div className="row mb-4">
            <div className="col-6 mb-2">
              <label className="text-muted d-block small">Condition</label>
              <strong>{item.isnew ? 'New' : 'Used'}</strong>
            </div>
            <div className="col-6 mb-2">
              <label className="text-muted d-block small">City</label>
              <strong>{CITIES[item.city] || 'N/A'}</strong>
            </div>
            <div className="col-6 mb-2">
              <label className="text-muted d-block small">Category</label>
              <strong>{CATEGORIES[item.category] || 'N/A'}</strong>
            </div>
            <div className="col-6 mb-2">
              <label className="text-muted d-block small">Shipping</label>
              <strong>{item.can_ship ? 'Available' : 'Pickup Only'}</strong>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="fw-bold border-bottom pb-2 mb-3">Description</h5>
            <p className="description-text" style={{ whiteSpace: 'pre-line', color: '#555', lineHeight: '1.6' }}>
              {item.description}
            </p>
          </div>
        </div>
      </div>

      {/* CTA Box - Full Width */}
      <div className="row mt-2">
        <div className="col-12">
          <div className="cta-box bg-light p-4 rounded-3 border text-center shadow-sm">
            <h4 className="mb-2 fw-bold">Interested in this item?</h4>
            <p className="text-muted mb-4">Contact the seller and finalize the deal through our app.</p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <a className="download-button" href="https://play.google.com/store/apps/details?id=com.toytrade.exchange&referrer=utm_source%3Dwebsite%26">
                <img src="/images/download_google.svg" alt="Download on Google Play" />
              </a>
              <a className="download-button" href="https://apps.apple.com/pk/app/toy-trade-pakistan/id6742099837?source=website">
                <img src="/images/download_appstore.svg" alt="Download on the App Store" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Similar Items Section */}
      <SimilarItemsBar itemTitle={item.title} currentItemId={item.id} />

      {/* App Banner at the bottom */}
      <AppBanner className="mt-5" />

      {/* Modal for Full Image */}
      {isModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setIsModalOpen(false)}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered h-100 my-0">
            <div className="modal-content bg-transparent border-0 h-100">
              <div className="modal-body d-flex align-items-center justify-content-center p-0">
                <button
                  type="button"
                  className="btn-close btn-close-white position-absolute top-0 end-0 m-4"
                  style={{ zIndex: 1060 }}
                  onClick={() => setIsModalOpen(false)}
                  aria-label="Close modal"
                ></button>

                {item.images && item.images.length > 1 && (
                  <>
                    <button
                      className="modal-nav-btn modal-nav-prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveImage((prev) => (prev - 1 + item.images.length) % item.images.length);
                      }}
                      aria-label="Previous image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16" aria-hidden="true">
                        <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                      </svg>
                    </button>
                    <button
                      className="modal-nav-btn modal-nav-next"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveImage((prev) => (prev + 1) % item.images.length);
                      }}
                      aria-label="Next image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16" aria-hidden="true">
                        <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                      </svg>
                    </button>
                  </>
                )}

                <img
                  src={currentImageUrl}
                  className="img-fluid rounded shadow"
                  alt={item.title}
                  style={{ maxHeight: '90vh', objectFit: 'contain' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemView;
